import { createSelector } from '@ngrx/store';
import { DataBag } from '../../../store/selectors';
import { EditorFeature } from '../editor.actions';
import { CanvasPosition, EditorState } from '../reducer/editor.reducer';
import { get as _get, set as _set, pick } from 'lodash';
import {
  AnimationItem,
  AnimationKeys,
  AnimationValue,
  CircleShapeDescriptor,
  IfPanelState,
  PathShapeDescriptor,
  SVGAttributes,
  Scene,
  TextShapeDescriptor,
  HandShapeNewDescriptor,
  FillAnimation,
  ShapeTransform,
  GeneralShapeDescriptor,
  PrimitiveValue,
  RectangleShapeDescriptor,
  TextAnimationConfig,
  CellAlignment,
  FileDescriptor,
  ProjectDescriptor,
  SceneBackgroundDefinition,
} from '../../../elements/resource/types/shape.type';
import { selectAnimationFeature } from '../../animation/store/animation.reducer';
import {
  AttributePanelState,
  ShapeAttributeState,
} from '../../../services/animation/animation.types';
import { ArmShapeDescriptor } from '../../shape/shapes/path-shape/arm-section/arm-shape-new';
import { selectProjectFeature } from '../../../projects/project.selector';
import { ResourceData } from '../../../elements/resource/resource.types';

const asc = (v1, v2) => (v1 > v2 ? 1 : -1);

const getAbsoluteCoords = (x: number, y: number, position: CanvasPosition) => {
  // -- // -- //
  const { x: xc, y: yc, scale } = position;
  x /= scale.x;
  y /= scale.y;

  return {
    x: (-xc + x) / scale.x,
    y: (-yc + y) / scale.y,
  };
};

const getRelativeCoords = (x: number, y: number, position: CanvasPosition) => {
  // -- // -- // -- //
  const { x: xc, y: yc, scale } = position;
  x *= scale.x;
  y *= scale.y;
  return { x: xc + x, y: yc + y };
};

export const selectEditorFeature = (state: DataBag) =>
  state[EditorFeature] as EditorState;

export const selectAnimationsByResource = createSelector(
  selectEditorFeature,
  ({ animationsByResourceByFrame }) => animationsByResourceByFrame,
);

export const selectAnimationConfigs = (animationType: 'appear' | 'disappear') =>
  createSelector(selectEditorFeature, ({ selectedShapes, descriptors }) => {
    const object = {} as any;

    Object.keys(selectedShapes).map(IRI => {
      if (!descriptors[IRI]) {
        return;
      }

      // -- // -- // -- // -- //

      const { type: shapeType, baseAnimations } = descriptors[IRI];
      const animationConfig = baseAnimations?.[animationType];

      if (shapeType == 'text-shape') {
        object.textShapePresent = true;
      }

      if (!animationConfig) {
        return;
      }

      const { type, float, scale } = animationConfig;
      object.type ||= {};
      object.type[type] = true;

      object.motion ||= {};

      if (float) {
        object.motion.float ||= {};
        object.motion.float[float] = float;
      }

      if (scale) {
        object.motion.scale ||= {};
        object.motion.scale[scale] = scale;
      }

      // -- // -- // -- // -- // -- // -- // -- // -- // -- //
      switch (shapeType) {
        case 'text-shape':
          const { by } = animationConfig as TextAnimationConfig;
          if (by) {
            object.by ||= {};
            object.by[by] = true;
          }
          break;
        default:
          break;
      }
    });

    return object;
  });

export const selectResources = createSelector(
  selectEditorFeature,
  ({ shapes, files }) => ({
    ...shapes,
    ...files,
  }),
);

export const selectCurrentFontFamily = createSelector(
  selectEditorFeature,
  ({ currentProjectID, projects, descriptors }) =>
    (descriptors[IdToIRI(currentProjectID)] as ProjectDescriptor)?.fontFamily ||
    (
      projects[IdToIRI(currentProjectID)]?.literals
        ?.descriptor as ProjectDescriptor
    )?.fontFamily ||
    'Arial',
);

export const selectCurrentFileIRI = createSelector(
  selectEditorFeature,
  ({ currentFileIRI }) => currentFileIRI,
);

export const constraintsByIRI = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    ({ descriptors }) => descriptors[IRI]?.constraints,
  );

export const selectCurrentAnimationId = createSelector(
  selectEditorFeature,
  ({ currentAnimationId }) => currentAnimationId,
);

export const selectImagePrompt = createSelector(
  selectEditorFeature,
  ({ imagePrompt }) => imagePrompt,
);
export const selectExportIsInProgress = createSelector(
  selectEditorFeature,
  ({ exportIsInProgress }) => exportIsInProgress,
);
export const selectRectangleRadius = (IRI: string) =>
  createSelector(selectEditorFeature, ({ currentDescriptors }) => {
    // TODO - make sure that no coalescing is needed
    return (currentDescriptors[IRI] as RectangleShapeDescriptor)?.r;
  });

export const isHiddenShape = (IRI: string) =>
  createSelector(selectEditorFeature, ({ hiddenShapes }) => hiddenShapes[IRI]);
export const selectCurrentFileScenes = createSelector(
  selectEditorFeature,
  selectProjectFeature,
  (state, { files, currentFileIRI: currentFileID }) => {
    const currentFile = files[currentFileID];
    const currentFileIRI = currentFile?.relationships?.childOf
      ? (currentFile?.relationships?.childOf as ResourceData).IRI
      : currentFileID;
    return Object.values(files)
      .filter(
        file =>
          (file?.relationships?.childOf as ResourceData)?.IRI ===
          currentFileIRI,
      )
      .sort((a, b) => asc(a.literals.sceneIndex, b.literals.sceneIndex));
  },
);

export const selectSceneSelectionInterval = createSelector(
  selectEditorFeature,
  state => {
    const selectedSceneIRIs = Object.keys(state.selectedScenes || {});

    // console.log('selectedSceneIRIs', selectedSceneIRIs);
    const min = Math.min(
      ...selectedSceneIRIs
        .filter(IRI => state.files[IRI])
        .map(IRI => state.files[IRI]?.literals.descriptor.index),
    );

    const max = Math.max(
      ...selectedSceneIRIs.map(
        IRI => state.files[IRI]?.literals.descriptor.index,
      ),
    );

    return [min, max] as [number, number];
  },
);

export const selectSelectedSceneBackgrounds = createSelector(
  selectEditorFeature,
  state => {
    const selectedSceneIRIs = Object.keys(state.selectedScenes || {});
    // -- // -- //

    return selectedSceneIRIs.reduce(
      (obj, IRI) => {
        const bgId = (state.files[IRI].literals.descriptor as FileDescriptor)
          .backgroundDefinitionId;

        // -- //
        const projectIRI = IdToIRI(state.currentProjectID);
        const bgDef = (
          state.projects[projectIRI].literals.descriptor as ProjectDescriptor
        ).backgroundDefinitions[bgId];

        // -- // -- //

        // if (bgDef) {
        //   obj[IRI] = state.project.literals.descriptor.segmentsById[bgId];
        // }
        return obj;
      },
      {} as Record<string, SceneBackgroundDefinition>,
    );
  },
);

export const selectRootSelectedFileIRI = createSelector(
  selectEditorFeature,
  selectProjectFeature,
  (state, { files, currentFileIRI: currentFileID }) => {
    const currentFile = files[currentFileID];
    const currentFileIRI = currentFile?.relationships?.childOf
      ? (currentFile?.relationships?.childOf as ResourceData).IRI
      : currentFileID;
    return currentFileIRI;
  },
);

export const selectEditorState = (key: string) =>
  createSelector(selectEditorFeature, state => state?.editorState[key]);

export const selectMaskChildren = (IRI: string) =>
  createSelector(selectEditorFeature, ({ shapes }) => {
    if (!shapes[IRI]) {
      return [];
    }
    const maskChildren = shapes[IRI].relationships.maskChildren;
    return Object.keys(maskChildren || {})
      .map(shapeIRI => shapes[shapeIRI])
      .filter(shape => !!shape);
  });

export const selectShapes = createSelector(
  selectEditorFeature,
  state => state.shapes,
);

export const selectCurrentShapes = createSelector(
  selectProjectFeature,
  selectEditorFeature,
  ({ files, currentFileIRI }, { shapes }) => {
    const currentFile = files[currentFileIRI];

    return {
      // -- // -- // -- //
    };

    // const currentFileIRI = currentFile?.relationships?.childOf
    //   ? (currentFile?.relationships?.childOf as ResourceData).IRI
    //   : currentFileID;
    // -- // -- // -- //
    // return Object.values(shapes).filter(
    //   shape => shape.relationships?.childOf?.IRI === currentFileIRI,
    // );
  },
);

export const getCurrentFile = createSelector(
  selectEditorFeature,
  ({ files, currentFileIRI }) => files?.[currentFileIRI],
);

export const getFiles = createSelector(
  selectEditorFeature,
  ({ files, currentFileIRI }) => files[currentFileIRI],
);

export const selectBulkUpdates = createSelector(
  selectEditorFeature,
  state => state.bulkUpdates,
);

export const isAnimationMode = createSelector(
  selectEditorFeature,
  selectAnimationFeature,
  ({ noAnimationMode }, { currentAnimationId }) =>
    !!currentAnimationId && !noAnimationMode,
);

import { selectUserFeature } from '../../../user/user.selector';
import { ExtendedComment } from '../../../elements/resource/types/shape.type';
import { selectOrganisationFeature } from '../../../organisation/organisation.selector';
import {
  AnimationSidePanelComponent,
  AnimationSidePanelState,
} from '../../shape-attribute-panel/animation-side-panel/animation-side-panel.component';
import { IdToIRI } from '../../../util/iri';
import { TextAnimationStep } from '../../animation/text-animation.service';
import { GloveDefinition } from '../../shape/background.service';

export const selectCanvasPosition = createSelector(
  selectEditorFeature,
  ({ canvasPosition }) => canvasPosition,
);

export const selectCommentToShow = createSelector(
  selectEditorFeature,
  selectUserFeature,
  (
    {
      selectedShapes,
      descriptors,
      canvasPosition,
      currentShapeTransforms,
      commentDrafts,
    },
    userState,
  ): null | {
    IRI: string;
    position: { x: number; y: number };
    comments: ExtendedComment[];
    draft: string;
  } => {
    if (Object.keys(selectedShapes).length !== 1) {
      return null;
    }
    const [shapeIRI] = Object.keys(selectedShapes);
    const descriptor = descriptors[shapeIRI];
    if (descriptor?.type !== 'comment-shape') {
      return null;
    }
    const { x, y } = currentShapeTransforms[shapeIRI].translate;
    const users = userState.allUsers || {};
    // console.log('users', users);
    const comments = ((descriptor as any).comments || []).map(
      (comment: any) => {
        const user = users[comment.user];
        return {
          ...comment,
          userName: user ? `${user.firstName} ${user.lastName}` : comment.user,
          // avatar: user && user.avatar ? user.avatar : undefined,
        } as ExtendedComment;
      },
    );
    return {
      IRI: shapeIRI,
      position: getRelativeCoords(x, y, canvasPosition),
      comments,
      draft: commentDrafts[shapeIRI],
    };
  },
);

export const multiplicationByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => ({
    x: state.descriptors[IRI]?.multiplicationX,
    y: state.descriptors[IRI]?.multiplicationY,
  }));

export const dropShadowByIRI = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    state => state.descriptors[IRI]?.dropShadow,
  );

export const originalShapeTransforms = createSelector(
  selectEditorFeature,
  state => state.originalShapeTransforms,
);
export const currentShapeTransforms = createSelector(
  selectEditorFeature,
  state => state.currentShapeTransforms,
);
export const baseShapeTransforms = createSelector(
  selectEditorFeature,
  state => state.baseShapeTransforms,
);
export const originalDescriptors = createSelector(
  selectEditorFeature,
  state => state.originalDescriptors,
);
export const currentDescriptors = createSelector(
  selectEditorFeature,
  state => state.currentDescriptors,
);
export const currentSVGAttributes = createSelector(
  selectEditorFeature,
  state => state.currentSVGAttributes,
);
export const baseSvgAttributes = createSelector(
  selectEditorFeature,
  state => state.baseSvgAttributes,
);
export const containerMenu = createSelector(
  selectEditorFeature,
  state => state.containerMenu,
);

export const selectContainerMenuByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => state.containerMenu?.[IRI]);

export const baseSVGAttributesByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => state.baseSvgAttributes[IRI]);

export const baseShapeTransformByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => state.baseShapeTransforms[IRI]);

export const baseDescriptorByIRI = <T = GeneralShapeDescriptor>(IRI: string) =>
  createSelector(selectEditorFeature, state => state.descriptors[IRI] as T);

export const currentSVGAttributesByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => state.currentSVGAttributes[IRI]);

export const selectDescriptors = createSelector(
  selectEditorFeature,
  state => state.descriptors,
);

export const colorPalette = createSelector(
  selectEditorFeature,
  state => state.colorPalette,
);

export const amISelected = (IRI: string) =>
  createSelector(selectEditorFeature, state => !state.selectedShapes[IRI]);

export const selectCrudEvents = createSelector(
  selectEditorFeature,
  state => state.crudEvents,
);

export const bBoxes = createSelector(
  selectEditorFeature,
  state => state.bBoxes,
);

export const getShapeResourceData = (
  IRI: string,
  transforms: Record<string, ShapeTransform>,
  descriptors: Record<string, GeneralShapeDescriptor>,
  svgAttributes: Record<string, SVGAttributes>,
  shapes: Record<string, ResourceData>,
  updatesByState: Record<string, any>,
  animations: any,
): ResourceData => {
  let override = {};
  // console.log('get.shape.resource.data', IRI);
  const relationships: Record<string, any> = {};
  if (!descriptors[IRI] || !transforms[IRI]) {
    return;
  }
  const { x, y } = transforms[IRI].scale || { x: 0, y: 0 };
  switch (descriptors[IRI].type) {
    case 'rectangle-shape':
      override = {
        width: x,
        height: y,
      };
      break;
    case 'circle-shape':
      override = {
        rx: (x as number) / 2,
        ry: (y as number) / 2,
      };
      break;
    // case 'imported-shape':
    //   relationships.instanceOf = (
    //     descriptors[IRI] as ImportedShapeDescriptor
    //   ).shapeIRI;
    //   break;
  }
  return {
    ...shapes[IRI],
    type: 'nw:Shape',
    literals: {
      ...shapes[IRI].literals,
      descriptor: {
        ...descriptors[IRI],
        position: {
          ...transforms[IRI].translate,
          scale: transforms[IRI]?.scale,
          rotation: transforms[IRI]?.rotation,
        },
        svgAttributes: svgAttributes[IRI],
        _animationsByKey: Object.entries(animations[IRI] || {}).reduce(
          (object, [animationId, animations]) => {
            object[animationId] = Object.entries(animations || {}).map(
              ([key, value]) => ({ ...value, key }),
            );
            return object;
          },
          {},
        ),
        updatesByState: updatesByState?.[IRI],
        ...override,
        // fixPosition: false,
        // 'show-hide': undefined,
      },
    },
    relationships: {
      ...relationships,
    },
  };
};

// export const getShapesToUpdate = createSelector(
//   selectEditorFeature,
//   selectAnimationFeature,
//   (
//     {
//       patchedShapes,
//       descriptors,
//       baseShapeTransforms,
//       baseSvgAttributes,
//       shapes,
//       updatesByState,
//       relationshipPatches,
//       deletedShapes,
//       newShapes,
//     },
//     { animationsByShape },
//   ) => {
//     // console.log('get-shapes-to-update', { patchedShapes });

//     const patches = Object.keys(patchedShapes).map(IRI =>
//       getShapeResourceData(
//         IRI,
//         baseShapeTransforms,
//         descriptors,
//         baseSvgAttributes,
//         shapes,
//         updatesByState,
//         animationsByShape,
//         relationshipPatches,
//       ),
//     );
//     const posts = Object.keys(newShapes).map(IRI =>
//       getShapeResourceData(
//         IRI,
//         baseShapeTransforms,
//         descriptors,
//         baseSvgAttributes,
//         shapes,
//         updatesByState,
//         animationsByShape,
//         relationshipPatches,
//       ),
//     );

//     return {
//       patches,
//       posts,
//       deletes: Object.keys(deletedShapes).map(IRI => ({ IRI })),
//     };
//   },
// );
export const selectOverlay = createSelector(
  selectEditorFeature,
  state => state.overlay,
);

export const selectCurrentTextAnimation = createSelector(
  selectEditorFeature,
  state =>
    (state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor)
      .textAnimation,
);

export const selectCurrentImageAnimation = createSelector(
  selectEditorFeature,
  state =>
    (state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor)
      .imageAnimation,
);

export const selectCurrentTextColor = createSelector(
  selectEditorFeature,
  state =>
    (state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor)
      ?.textColor,
);

export const selectCurrentBackgroundId = createSelector(
  selectEditorFeature,
  state => {
    const fileDesc = state.files[state.currentFileIRI]?.literals
      .descriptor as FileDescriptor;
    return fileDesc?.backgroundDefinitionId;
  },
);

export const selectCurrentFileDescritor = createSelector(
  selectEditorFeature,
  state =>
    state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor,
);

export const selectCurrentTextAnimationStep = createSelector(
  selectEditorFeature,
  state =>
    (state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor)
      ?.textAnimationStep,
);

export const selectCurrentFileDescriptorByKey = (key: keyof FileDescriptor) =>
  createSelector(
    selectEditorFeature,
    state =>
      (
        state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor
      )[key],
  );

export const selectCurrentProjectDescriptorKey = (
  key: keyof ProjectDescriptor,
) =>
  createSelector(
    selectEditorFeature,
    state =>
      (
        state.projects[IdToIRI(state.currentProjectID)]?.literals
          .descriptor as ProjectDescriptor
      )[key],
  );

export const selectCurrentProjectDescriptor = createSelector(
  selectEditorFeature,
  state =>
    state.projects[IdToIRI(state.currentProjectID)]?.literals
      .descriptor as ProjectDescriptor,
);

export const selectCurrentBackgroundDefinitionId = createSelector(
  selectEditorFeature,
  state => {
    const fileDesc = state.files[state.currentFileIRI]?.literals
      .descriptor as FileDescriptor;
    return fileDesc?.backgroundDefinitionId;
  },
);

export const selectRootshapesInOrder = createSelector(
  selectEditorFeature,
  state =>
    Object.values(state.files).sort(
      (a, b) => a.literals.descriptor.index - b.literals.descriptor.index,
    ),
);

export const _selectCurrentBackgroundDefinition = createSelector(
  selectEditorFeature,
  // selectRootshapesInOrder,
  // (state, rootshapes) => {
  state => {
    const { currentFileIRI } = state;
    // Selecting the rootShapes before the current one

    const currentRootShape = state.files[currentFileIRI];
    if (!currentRootShape) {
      return;
    }

    const currentRootShapeDescriptor = currentRootShape.literals
      .descriptor as FileDescriptor;

    const currentBackgroundId =
      currentRootShapeDescriptor.backgroundDefinitionId;

    if (!currentBackgroundId) {
      // -- // -- // -- // -- //
      return;
    }

    const currentProject = state.projects[IdToIRI(state.currentProjectID)];
    const currentBackgroundDefinition = (
      currentProject.literals.descriptor as ProjectDescriptor
    ).backgroundDefinitions[
      (currentRootShape.literals.descriptor as FileDescriptor)
        .backgroundDefinitionId
    ] as SceneBackgroundDefinition;

    if (!currentBackgroundDefinition) {
      return;
    }

    if (currentBackgroundDefinition.type !== 'gloves') {
      return [currentBackgroundId, currentBackgroundDefinition];
    }

    const rootShapesInOrder = Object.values(state.files).sort(
      (a, b) => a.literals.descriptor.index - b.literals.descriptor.index,
    );

    const rootShapesBeforeCurrent = rootShapesInOrder.filter(
      r =>
        r.literals.descriptor.index <
        currentRootShape.literals.descriptor.index,
    );

    const rootShapesBeforeCurrentInReverseOrder = [
      ...rootShapesBeforeCurrent,
    ].reverse();

    // I am looking the first rootShape going backward that has the same background definition id
    // and it has gloveActions defined. If no such is find then I simply return the current bg definition
    // If found -> then I update the current definition.

    /* console.log(
      'rootShapesBeforeCurrentInReverseOrder',
      rootShapesBeforeCurrentInReverseOrder,
    ); */
    for (const rootShape of rootShapesBeforeCurrentInReverseOrder) {
      const rootShapeDescriptor = rootShape.literals
        .descriptor as FileDescriptor;

      if (
        rootShapeDescriptor.backgroundDefinitionId ===
        currentRootShapeDescriptor.backgroundDefinitionId
      ) {
        // Check if there's gloveActions
        const gloveActions = getGloveActionsInOrder(rootShapeDescriptor);
        if (gloveActions.length) {
          const last = gloveActions[gloveActions.length - 1];
          // -- // -- //
          /* console.log('last', last);
          console.log(
            'currentBackgroundDefinition',
            currentBackgroundDefinition.options,
          ); */
          return [
            currentBackgroundId,
            {
              ...currentBackgroundDefinition,
              options: Object.entries(
                currentBackgroundDefinition.options,
              ).reduce((acc, [id, cur]) => {
                acc[id] = {
                  ...cur,
                  ...(last[id] || {}),
                };
                return acc;
              }, {}),
            },
          ];
        }
        return [currentBackgroundId, currentBackgroundDefinition];
      }
    }
    return [currentBackgroundId, currentBackgroundDefinition];
  },
);

function getGloveActionsInOrder(
  descriptor: FileDescriptor,
): Record<string, GloveDefinition>[] {
  const gloveActions = findBgGloveActions(descriptor.textAnimationStep);
  // -- // -- //
  return gloveActions
    .sort((a, b) => a.index - b.index)
    .map(a => a.action.bgGloveMotion);
  // console.log('gloveActionsInOrder', this.gloveActionsInOrder);
}

function findBgGloveActions(node: TextAnimationStep, path = '') {
  const results = [];

  // If the node is not an object, nothing to do
  if (node === null || typeof node !== 'object') return results;

  // ---- 1. Check if this node has an action with bgGloveMotion ----
  if (node.action && node.action.bgGloveMotion !== undefined) {
    results.push({
      index: node.action.index,
      //path: path ? path + '.action' : 'action',
      action: node.action,
    });
  }

  // ---- 2. Traverse all keys in this object ----
  for (const key of Object.keys(node)) {
    const value = node[key];

    if (Array.isArray(value)) {
      // Handle arrays (like parallel)
      value.forEach((item, index) => {
        const childPath = path
          ? `${path}.${key}[${index}]`
          : `${key}[${index}]`;
        results.push(...findBgGloveActions(item, childPath));
      });
    } else if (value !== null && typeof value === 'object') {
      // Handle nested objects
      const childPath = path ? `${path}.${key}` : key;
      results.push(...findBgGloveActions(value, childPath));
    }
  }
  return results;
}
export const selectCurrentBackgroundDefinition = createSelector(
  selectEditorFeature,
  state => {
    const projectIRI = IdToIRI(state.currentProjectID);
    const defs =
      (state.projects[projectIRI]?.literals?.descriptor as ProjectDescriptor)
        ?.backgroundDefinitions || {};
    const fileDesc = state.files[state.currentFileIRI]?.literals
      .descriptor as FileDescriptor;
    const id = fileDesc?.backgroundDefinitionId;
    return id ? (defs[id] as SceneBackgroundDefinition) : undefined;
  },
);

export const selectCurrentProjectBackgroundDefinitions = createSelector(
  selectEditorFeature,
  state => {
    const projectIRI = IdToIRI(state.currentProjectID);
    return (
      (state.projects[projectIRI]?.literals?.descriptor as ProjectDescriptor)
        ?.backgroundDefinitions || {}
    );
  },
);

// Alias requested name for Background tab bindings
export const currentBackgroundDefinition = selectCurrentBackgroundDefinition;

export const selectCurrentSceneType = createSelector(
  selectEditorFeature,
  state => {
    const currentFileDescritor = state.files[state.currentFileIRI]?.literals
      .descriptor as FileDescriptor;
    // -- // return currentFileDescritor?.type; // -- //

    if (!currentFileDescritor) {
      return 'none';
    }

    const contents = Object.values(currentFileDescritor?.content || {});
    // console.log('selectCurrentSceneType > contents', contents);
    switch (contents.length) {
      case 0:
        return 'none';
      case 1:
        const [first] = contents;
        if (first.text) {
          return 'text';
        } else if (first.imageUrl) {
          return 'image';
        } else if (first.videoUrl) {
          return 'video';
        } else {
          return 'none';
        }
      default:
        return 'container';
    }
  },

  // (state.files[state.currentFileIRI]?.literals.descriptor as FileDescriptor)
  //   .type,
);

export const selectCurrentFileResourceData = createSelector(
  selectProjectFeature,
  selectEditorFeature,
  selectAnimationFeature,
  (
    { files, currentFileIRI: currentFileID },
    { bBoxes, states },
    { mainFrame, functions },
  ) => {
    const file = files[currentFileID];
    if (!file) {
      return;
    }

    let xMin = Infinity,
      yMin = Infinity,
      xMax = -Infinity,
      yMax = -Infinity;

    if (Object.keys(bBoxes).length) {
      Object.values(bBoxes).map(({ x, y, width, height }) => {
        xMin = x < xMin ? x : xMin;
        yMin = y < yMin ? y : yMin;
        xMax = x + width > xMax ? x + width : xMax;
        yMax = y + height > yMax ? y + height : yMax;
      });
    }

    const offset = {
      x: xMin,
      y: yMin,
      width: xMax - xMin,
      height: yMax - yMin,
    };

    return {
      IRI: file.IRI,
      literals: {
        descriptor: {
          ...(file.literals.descriptor || {}),
          offset,
          animationFrame: mainFrame,
          animationFunctions: functions,
          states,
        },
      },
    };
  },
);
export const selectUpdatesByState = createSelector(
  selectEditorFeature,
  state => state.updatesByState,
);
export const deletedShapes = createSelector(
  selectEditorFeature,
  state => state.crudEvents.deletes,
);
export const isPatchLoading = createSelector(
  selectEditorFeature,
  state => state.isPatchLoading,
);
export const selectSelectedShapes = createSelector(
  selectEditorFeature,
  state => state.selectedShapes,
);
export const selectedShapeList = createSelector(selectEditorFeature, state =>
  Object.keys(state.selectedShapes || {}),
);

export const selectSelectedScenes = createSelector(
  selectEditorFeature,
  state => state.selectedScenes || {},
);

export const selectScenes = createSelector(
  selectEditorFeature,
  ({ scenes }): Scene[] => {
    return scenes;
  },
);
export const selectChangeTookPlace = createSelector(
  selectEditorFeature,
  state => state.changeTookPlace,
);
export const selectStates = createSelector(
  selectEditorFeature,
  ({ states }) => states || [],
);

export const selectedSelectedState = createSelector(
  selectEditorFeature,
  ({ selectedState }) => selectedState,
);
export const isSaveActive = createSelector(
  selectEditorFeature,
  selectAnimationFeature,
  (state, animationState) => {
    return (
      !!Object.keys({
        ...state.crudEvents.posts,
        ...state.crudEvents.deletes,
        ...state.crudEvents.patches,
      }).length ||
      state.fileChanged ||
      animationState.animationChanged
    );
  },
);

export const isShapeSelected = (IRI: string) =>
  createSelector(selectEditorFeature, state => !!state.selectedShapes[IRI]);

export const selectedShapes = createSelector(selectEditorFeature, state =>
  Object.keys(state.selectedShapes),
);
export const _selectedShapes = createSelector(
  selectEditorFeature,
  state => state.selectedShapes,
);

export const showComponentSearch = createSelector(
  selectEditorFeature,
  state => state.showComponentSearch,
);

export const selectCurrentShapeTranslate = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    state => state.currentShapeTransforms[IRI]?.translate,

    // {
    // const { updatesByState, selectedState } = state;

    // if (!state.currentShapeTransforms[IRI]?.translate) {
    //   return;
    // }

    // let selectedPosition: Partial<ShapePosition> = {};

    // if (selectedState && updatesByState[IRI]?.[selectedState]?.position) {
    //   // console.log(
    //   //   '---- selectCurrentShapeTranslate -----', IRI,
    //   //   updatesByState[IRI]?.[selectedState]?.position,
    //   // );
    //   selectedPosition = updatesByState[IRI]?.[selectedState]?.position;
    //   // return updatesByState[IRI]?.[selectedState]?.position; //
    // }

    // return {
    //   x: selectedPosition.x || state.currentShapeTransforms[IRI]?.translate.x,
    //   y: selectedPosition.y || state.currentShapeTransforms[IRI]?.translate.y,
    // };
  );

export const selectCurrentShapeScale = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    const { updatesByState, selectedState } = state;
    if (
      selectedState &&
      (updatesByState[IRI]?.[selectedState]?.position as ShapeTransform)?.scale
    ) {
      return (updatesByState[IRI]?.[selectedState]?.position as ShapeTransform)
        ?.scale;
    }

    return state.currentShapeTransforms[IRI]?.scale;
    // const { type } = state.descriptors[IRI] || {};
    // switch (type) {
    //   case 'circle-shape':
    //     return {
    //       x: 2 * (descriptor as CircleShapeDescriptor).rx,
    //       y: 2 * (descriptor as CircleShapeDescriptor).ry,
    //     };
    //     default:
    //     return state.currentShapeTransforms[IRI]?.scale;
    // }
  });

export const selectCurrentShapeRotation = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    state => state.currentShapeTransforms[IRI]?.rotation,
  );

export const selectShapeTransforms = createSelector(
  selectEditorFeature,
  state => state.baseShapeTransforms,
);

export const selectVisitStack = createSelector(
  selectEditorFeature,
  state => state.visitStack,
);

// export const getProjectColors = createSelector(selectEditorFeature, state => {
//   return Object.entries(
//     state.project?.literals?.descriptor?.colorPalette || {},
//   ).map(([label, color]) => ({ label, color }));
// });

// export const files = createSelector(selectEditorFeature, state =>
//   Object.values(state.files).sort((f1, f2) =>
//     f1.literals.label > f2.literals.label ? 1 : -1,
//   ),
// );

// export const selectCurrentScenes // -- // -- //

export const isProjectLoading = createSelector(
  selectEditorFeature,
  state => state.isPatchLoading,
);

export const scenesAreLoaded = createSelector(
  selectEditorFeature,
  state => state.scenesAreLoaded,
);

export const isFileLoading = createSelector(
  selectEditorFeature,
  state => !!state.isSaveLoading,
);

// export const currentProjectID = createSelector(selectEditorFeature, state => {
//   return state.project?.IRI?.split('#')?.[1];
// });

// export const currentProject = createSelector(selectEditorFeature, state => {
//   return state.project;
// });

export const selectAnimationSidePanelState = createSelector(
  selectEditorFeature,
  ({
    selectedShapes,
    descriptors,
    currentFileIRI,
  }): AnimationSidePanelState => {
    if (Object.keys(selectedShapes).length > 1) {
      return {
        type: 'multi-selection',
      };
    }

    let [shapeIRI] = Object.keys(selectedShapes);

    shapeIRI ||= currentFileIRI;

    if (!shapeIRI || !descriptors) {
      return;
    }

    if (!descriptors[shapeIRI]) {
      return;
    }

    const { type, baseAnimations } = descriptors[shapeIRI];
    switch (type) {
      case 'container-shape':
        return {
          IRI: shapeIRI,
          type: 'container',
          appear: baseAnimations?.appear || {},
          disappear: baseAnimations?.disappear || {},
        };
      case 'text-shape':
        return {
          IRI: shapeIRI,
          type: 'text',
          appear: (baseAnimations?.appear as TextAnimationConfig) || {},
          disappear: (baseAnimations?.appear as TextAnimationConfig) || {},
        };
      default:
        return {
          IRI: shapeIRI,
          type: shapeIRI == currentFileIRI ? 'container' : 'item',
          appear: baseAnimations?.appear || {},
          disappear: baseAnimations?.disappear || {},
        };
    }
  },
);

export const isShapeSelect = (IRI: string) =>
  createSelector(selectEditorFeature, state => !!state.selectedShapes[IRI]);

export const rootShapeList = createSelector(selectEditorFeature, state => {});

export const shapeList = createSelector(
  selectProjectFeature,
  selectEditorFeature,
  ({ currentFileIRI }, { files, shapes, crudEvents, descriptors }) => {
    const file = files?.[currentFileIRI];

    return [file].filter(v => !!v);
    /* console.log(
      'shapeList > shapes',
      Object.keys(file.relationships.shapes || {}).length,
      'not-deletes',
      Object.keys(file.relationships.shapes || {}).filter(
        IRI => !crudEvents.deletes[IRI],
      ).length,
    ); */
    return Object.keys(file?.relationships.shapes || {})
      .filter(IRI => !crudEvents.deletes[IRI])
      .sort((s1, s2) => descriptors[s1]?.index - descriptors[s2]?.index)
      .map(shapeIRI => shapes[shapeIRI]);
  },
);

export const shapeAttribute = <T>(IRI: string, attributeKey: string) =>
  createSelector(
    selectEditorFeature,
    state => _get(state.descriptors, [IRI, attributeKey]) as T,
  );

export const showByIf = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    // const { sceneState: editorState } = state;

    return true;
    // const { current } = editorState || {};
    // if (!current) {
    //   return true;
    // }

    // const ifDefinition = state.descriptors[IRI]?.if;

    // if (!ifDefinition) {
    //   return true;
    // }

    // return ifDefinition[current];
  });

export const selectHoveredShape = createSelector(
  selectEditorFeature,
  state => state.hoveredShape,
);

export const selectFileDescriptor = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return state.files[IRI]?.literals?.descriptor as FileDescriptor;
  });
export const selectSceneFiles = createSelector(
  selectEditorFeature,
  selectProjectFeature,
  ({ files, currentProjectID }) => {
    // yx
    const projectIRI = IdToIRI(currentProjectID);
    return Object.values(files)
      .filter(file => file.relationships?.project == projectIRI) //
      .filter(
        file => (file.literals.descriptor as FileDescriptor).type == 'scene',
      )
      .sort(
        (a, b) => a.literals.descriptor?.index - b.literals.descriptor?.index,
      );
  },
);

export const selectInputFiles = createSelector(
  selectEditorFeature,
  selectProjectFeature,
  ({ files, currentProjectID }) => {
    const projectIRI = IdToIRI(currentProjectID);

    return Object.values(files)
      .filter(file => file.relationships?.project == projectIRI)
      .filter(
        file => (file.literals.descriptor as FileDescriptor).type != 'scene',
      );
  },
);

export const ifPanelState = createSelector(
  selectEditorFeature,
  ({ scenes, descriptors, selectedShapes }): IfPanelState => {
    if (!scenes || scenes.length == 1) {
      return {
        type: 'hide',
      };
    }

    // console.log('scenes', scenes);

    const allValues: Record<number, boolean> = {};
    const sceneValues = {};
    scenes.map(({ name: scene }) => (sceneValues[scene] = {}));

    Object.keys(selectedShapes).map(IRI => {
      const ifValue = descriptors[IRI]?.if;

      if (!ifValue) {
        allValues[1] = true;
        scenes.map(({ name: scene }) => {
          sceneValues[scene][1] = true;
        });
      } else if (Array.isArray(ifValue)) {
        // TODO - avoid that in the editor.effect
        // scenes.map(scene => {
        //   if (ifValue.includes(scene)) {
        //     sceneValues[scene][1] = true;
        //   } else {
        //     sceneValues[scene][0] = true;
        //   }
        // });
        // allValues[0] = true;
      } else if (ifValue) {
        scenes.map(({ name: scene }) => {
          if (ifValue[scene]) {
            sceneValues[scene][1] = true;
          } else {
            sceneValues[scene][0] = true;
          }
        });
      }
      // console.log('if-value', ifValue);
    });

    // console.log('scene-values', sceneValues);

    return {
      type: 'show',
      all: Object.keys(allValues).map(val => !!val),
      scenes: scenes.map(({ name: key }) => ({
        key,
        values: Object.keys(sceneValues[key]).map(val => !!+val),
      })),
    };
  },
);

export const getAttributeValueOfShape = (
  key: AnimationKeys,
  currentAnimationId: string,
  baseData: {
    transform?: ShapeTransform;
    svgAttributes?: SVGAttributes;
    descriptor?: GeneralShapeDescriptor;
  },
  animations: Record<string, Partial<Record<AnimationKeys, AnimationItem>>>,
): ShapeAttributeState => {
  if (currentAnimationId?.includes('_')) {
    const ids = currentAnimationId.split('_');
    currentAnimationId = ids[ids.length - 1];
  }
  if (animations?.[currentAnimationId]?.[key]) {
    const item = animations[currentAnimationId]?.[key];
    if (item.fcn) {
      return {
        type: 'function',
        value: item.fcn,
      };
    }
    return {
      type: 'animation',
      value: item.value,
    };
  }

  const { transform, svgAttributes, descriptor } = baseData;
  switch (key) {
    case 'translate':
    case 'scale':
    case 'rotation':
      return {
        type: 'base',
        value: transform[key],
      };

    case 'fill':
      return {
        type: svgAttributes?.fill ? 'base' : 'undefined',
        value: svgAttributes?.fill,
      };
    case 'stroke':
      return {
        type: svgAttributes?.stroke ? 'base' : 'undefined',
        value: svgAttributes?.stroke,
      };
    default:
      return {
        type: descriptor?.[key] ? 'base' : 'undefined',
        value: descriptor?.[key],
      };
  }
};

export const mergeObjects = (objects: AnimationValue[]) => {
  const returnObject: Record<string, PrimitiveValue[]> = {};

  objects.map(object => {
    Object.entries(object || {}).map(([key, value]) => {
      returnObject[key] ||= [];
      if (value !== undefined && !returnObject[key].includes(value)) {
        returnObject[key].push(value);
      }
    });
  });

  Object.entries(returnObject).map(([key, value]) => {
    returnObject[key] = value.sort((v1, v2) => (v1 > v2 ? 1 : -1));
  });

  return returnObject;
};

export const containerShapeDescriptor = createSelector(
  selectEditorFeature,
  ({ selectedShapes, descriptors }) => {
    const selectedIRIS = Object.keys(selectedShapes);
    if (selectedIRIS.length !== 1) {
      return;
    }

    const [IRI] = selectedIRIS;
    if (!IRI) {
      return null;
    }

    const descriptor = descriptors[IRI];
    if (!descriptor) {
      return null;
    }

    // undefined is a placeholder for the root shapes
    if (descriptor.type == 'container-shape' || descriptor.type == undefined) {
      return descriptor;
    }

    return;
  },
);

export const _attributeState = (attributeKey: AnimationKeys) =>
  createSelector(
    selectEditorFeature,
    selectAnimationFeature,
    (
      {
        selectedShapes,
        baseShapeTransforms,
        baseSvgAttributes,
        descriptors,
        noAnimationMode,
        selectedState,
        updatesByState,
        animationsByResourceByFrame,
      },
      { currentAnimationId, functions: animationFunctions },
    ): AttributePanelState => {
      const values = Object.keys(selectedShapes).map(shapeIRI =>
        getAttributeValueOfShape(
          attributeKey,
          noAnimationMode ? '' : currentAnimationId,
          {
            transform: baseShapeTransforms[shapeIRI],
            svgAttributes: baseSvgAttributes[shapeIRI],
            descriptor: descriptors[shapeIRI],
          },
          animationsByResourceByFrame[shapeIRI],
        ),
      );
      const isThereBase = !!values.find(({ type }) => type == 'base');
      const isThereAnimation = !!values.find(({ type }) => type == 'animation');
      const isThereFunction = !!values.find(({ type }) => type == 'function');
      const isThereUndefined = !!values.find(({ type }) => type == 'undefined');

      const optionCount = [
        isThereBase,
        isThereAnimation,
        isThereFunction,
        isThereUndefined,
      ].filter(val => val).length;

      if (optionCount > 1) {
        return { type: 'mixed' };
      }

      const functions = Object.keys(animationFunctions?.[attributeKey] || {});

      if (isThereFunction) {
        const selectedFunctions = values.reduce((prev, { value }) => {
          if (!prev.includes(value as string)) {
            prev.push(value as string);
          }
          return prev;
        }, [] as string[]);

        return {
          type: 'function',
          functions,
          selectedFunctions,
        };
      }

      if (isThereBase) {
        return {
          type: 'base',
          functions,
          value: mergeObjects(values.map(({ value }) => value)),
        };
      }

      if (isThereAnimation) {
        return {
          type: 'animation',
          functions,
          value: mergeObjects(values.map(({ value }) => value)),
        };
      }

      if (isThereUndefined) {
        return {
          type: 'undefined',
          functions,
          value: null,
        };
      }

      return null;
    },
  );

export const attributeState = (attributeKey: AnimationKeys) =>
  createSelector(
    selectEditorFeature,
    selectAnimationFeature,
    (
      {
        selectedShapes,
        baseShapeTransforms,
        baseSvgAttributes,
        descriptors,
        noAnimationMode,
        selectedState,
        updatesByState,
        animationsByResourceByFrame,
      },
      { currentAnimationId, functions: animationFunctions },
    ): AttributePanelState => {
      const selectedShapeIRIs = Object.keys(selectedShapes);
      if (selectedShapeIRIs.length == 0) {
        return {
          type: currentAnimationId ? 'animation' : 'base',
          state: 'undefined',
        };
      }
      let type: 'base' | 'animation';

      function getReturnValuesAndState(
        objects: any[],
      ): ['defined' | 'mixed', Record<string, any[]> | any[]] {
        const returnValue = {};
        const primitiveValues = [];
        let state: 'defined' | 'mixed' = 'defined';
        let primitiveValue = false;

        objects.map(object => {
          if (typeof object == 'object') {
            Object.entries(object || {}).map(([key, val]) => {
              returnValue[key] ||= [];
              if (!returnValue[key].includes(val)) {
                returnValue[key].push(val);
              }
            });
          } else {
            primitiveValue = true;
            if (!primitiveValues.includes(object)) {
              primitiveValues.push(object);
            }
          }
        });

        if (primitiveValue) {
          state = primitiveValues.length > 1 ? 'mixed' : 'defined';
          return [state, primitiveValues];
        }

        const deleteKeys = [];
        Object.keys(returnValue).map(key => {
          switch (returnValue[key].length) {
            case 0:
              deleteKeys.push(key);
              break;
            case 1:
              if (!returnValue[key][0]) {
                deleteKeys.push(key);
              }
              break;
            default:
              returnValue[key] = returnValue[key].sort(asc);
              break;
          }
        });

        deleteKeys.map(key => delete returnValue[key]);

        return [state, returnValue];
      }

      const fcnNames = Object.keys(animationFunctions?.[attributeKey] || {});
      const functions = fcnNames.length ? fcnNames : undefined;

      if (currentAnimationId && !noAnimationMode) {
        type = 'animation';

        console.log('current-animation-id', currentAnimationId);
        if (currentAnimationId.includes('_')) {
          const ids = currentAnimationId.split('_');
          currentAnimationId = ids[ids.length - 1];
        }
        console.log('current-animation-id > after', currentAnimationId);

        const items: AnimationItem[] = selectedShapeIRIs.map(IRI => {
          if (attributeKey == 'fill') {
            if (!animationsByResourceByFrame[IRI]?.[currentAnimationId]?.fill) {
              return;
            }

            return {
              key: attributeKey,
              value: {
                color:
                  animationsByResourceByFrame[IRI]?.[currentAnimationId]?.fill
                    .value,
                gradient: false,
              } as FillAnimation,
            };
          }

          return animationsByResourceByFrame[IRI]?.[currentAnimationId]?.[
            attributeKey
          ];
        });

        console.log('items', items);

        const isThereDefined = !!items.find(value => !!value && !value.fcn);
        const isThereUndefined = !!items.filter(value => !value).length;
        const isThereFunction = !!items.find(value => !!value?.fcn);

        const cnt = [isThereDefined, isThereUndefined, isThereFunction].filter(
          v => v,
        ).length;

        if (cnt > 1) {
          return { type, state: 'mixed', functions };
        }

        if (isThereUndefined) {
          return { type, state: 'undefined', functions };
        }

        if (isThereFunction) {
          const fcnKeys = items.reduce((object, item) => {
            object[item.fcn] = true;
            return object;
          }, {});

          if (Object.keys(fcnKeys).length > 1) {
            return {
              type,
              state: 'mixed',
              selectedFunctions: Object.keys(fcnKeys).sort(asc),
              isFunction: true,
              functions,
            };
          } else {
            const [fcn] = Object.keys(fcnKeys);

            if (fcn == 'original') {
              if (Object.keys(selectedShapes).length > 1) {
                return {
                  type,
                  state: 'mixed',
                  selectedFunctions: Object.keys(fcnKeys).sort(asc),
                  isFunction: true,
                  functions,
                };
              }
              const [IRI] = Object.keys(selectedShapes);
              let value: AnimationValue;
              switch (attributeKey) {
                case 'scale':
                  value = baseShapeTransforms[IRI].scale;
                  break;
                case 'translate':
                  value = baseShapeTransforms[IRI].translate;
                  break;
                case 'rotation':
                  console.log(
                    'rotation-state',
                    baseShapeTransforms[IRI].rotation,
                  );
                  value = baseShapeTransforms[IRI].rotation || { angle: 0 };
                  break;
                case 'stroke':
                  value = baseSvgAttributes[IRI].stroke;
                  break;
                  // case 'fill':
                  //   value = baseSvgAttributes[IRI].fill;

                  break;
              }
              // -- //
              return {
                type,
                state: 'defined',
                isFunction: true,
                selectedFunctions: [fcn],
                functions,
                value,
              };
            }

            return {
              type,
              state: 'defined',
              isFunction: true,
              selectedFunctions: [fcn],
              functions,
              value: animationFunctions?.[attributeKey]?.[fcn].value as any,
            };
          }
        }

        // there is no function just values //
        const values = items.map(item => item.value);
        const [state, value] = getReturnValuesAndState(values);
        return { type, state, value, functions };
      } else {
        let values: AnimationValue[];
        type = 'base';

        switch (attributeKey) {
          // case 'fill':
          //   values = selectedShapeIRIs.map(IRI => ({
          //     color: baseSvgAttributes[IRI]?.fill,
          //     gradient: !!baseSvgAttributes[IRI]?.gradient,
          //     gradientColor: baseSvgAttributes[IRI]?.gradientColor,
          //     gradientDirection: baseSvgAttributes[IRI]?.gradientDirection,
          //   })) as FillAnimation[];
          //   break;
          case 'stroke':
          case 'stroke-width':
          case 'opacity':
            values = selectedShapeIRIs.map(
              IRI => baseSvgAttributes[IRI]?.[attributeKey],
            );
            break;
          case 'translate':
            values = selectedShapeIRIs.map(IRI => {
              if (
                selectedState &&
                updatesByState[IRI]?.[selectedState]?.position?.x
              ) {
                return pick(updatesByState[IRI]?.[selectedState]?.position, [
                  'x',
                  'y',
                ]);
              }
              return baseShapeTransforms[IRI]?.[attributeKey];
            });
            break;
          case 'scale':
          case 'rotation':
            values = selectedShapeIRIs.map(IRI => {
              if (
                selectedState &&
                updatesByState[IRI]?.[selectedState]?.position?.[attributeKey]
              ) {
                console.log('------ hey > scale ------');
                return updatesByState[IRI]?.[selectedState]?.position?.[
                  attributeKey
                ];
              }
              return baseShapeTransforms[IRI]?.[attributeKey];
            });
            break;
          case 'config':
            values = selectedShapeIRIs
              .filter(IRI => descriptors[IRI]?.type == 'hand-shape')
              .map(IRI => descriptors[IRI]?.[attributeKey]);
            break;

          // case 'env':
          //   // -- //
          //   // values = selectedShapeIRIs
          //   //   .map(IRI => descriptors[IRI])
          //   //   .filter(descriptor => {
          //   //     return (
          //   //       descriptor.type == 'imported-shape' &&
          //   //       (descriptor as ImportedShapeDescriptor).baseShapeDescriptor
          //   //         .inputs?.length
          //   //     );
          //   //   })
          //   //   .map(descriptor => descriptor.env);
          //   values = selectedShapeIRIs
          //     .map(IRI => descriptors[IRI])
          //     .filter(descriptor => {
          //       return (
          //         descriptor.type == 'imported-shape' &&
          //         (descriptor as ImportedShapeDescriptor).baseShapeDescriptor
          //           .inputs?.length
          //       );
          //     })
          //     .map(descriptor => descriptor.env);
          //   break;

          default:
            values = selectedShapeIRIs.map(IRI => {
              return descriptors[IRI]?.[attributeKey];
            });
            break;
        }

        if (!values.length) {
          return { type: 'base', state: 'undefined' };
        }

        if (attributeKey == 'stroke') {
          // console.log('values', values);
        }

        const isThereDefined = !!values.find(value => !!value);
        const isThereUndefined = !!values.filter(value => !value).length;

        if (isThereDefined && isThereUndefined) {
          return { type: 'base', state: 'mixed', functions };
        }

        if (isThereUndefined && !isThereDefined) {
          return { type: 'base', state: 'undefined', functions };
        }

        if (attributeKey == 'handSections') {
          return {
            type,
            state: values.length ? 'defined' : 'undefined',
            value: values[0],
          };
        }

        const [state, value] = getReturnValuesAndState(values);
        return {
          type,
          state,
          value,
          functions,
        };
      }
    },
  );

export const onlyShapeType = (
  type:
    | 'file'
    | 'hand-shape'
    | 'hand-shape-next'
    | 'path-shape'
    | 'text-shape'
    | 'circle-shape'
    | 'arm-shape',
) =>
  createSelector(selectEditorFeature, ({ selectedShapes, descriptors }) => {
    return !Object.keys(selectedShapes).find(
      IRI => descriptors[IRI]?.type !== type,
    );
  });

export const selectCanvasBackgroundColor = createSelector(
  selectEditorFeature,
  selectProjectFeature,
  selectOrganisationFeature,
  (
    { currentSVGAttributes },
    { allProjects, currentProjectID, currentFileIRI },
    { allOrganisations, currentOrganisationID },
  ) => {
    const project = allProjects[currentProjectID];
    // const colorPalette = project?.colorPalette;
    const organisation = allOrganisations[currentOrganisationID];
    // console.log('project', project);
    const colorPalette = {
      ...(organisation?.colorPalette || {}),
      ...(project?.colorPalette || {}),
    };
    // console.log('organisation', organisation);
    const svgAttributes = currentSVGAttributes[currentFileIRI];
    // console.log('currentFileDescriptor', currentFileDescriptor); //
    // console.log('selectCanvasBackgroundColor - svgAttributes', svgAttributes);
    const bgColor = svgAttributes?.fill.color;
    // console.log('svgAttributes', svgAttributes);
    if (!bgColor) {
      return '#fff';
    }
    // -- //
    // console.log('bgColor', bgColor);
    return bgColor?.startsWith('$')
      ? colorPalette?.[bgColor.slice(2)]
      : bgColor;

    // return baseSvgAttributes[currentFileIRI]?.['canvas-background-color'];
  },
);

export const onlyShapeTypes = (types: string[]) =>
  createSelector(selectEditorFeature, ({ selectedShapes, descriptors }) => {
    return !Object.keys(selectedShapes).find(
      IRI => !types.includes(descriptors[IRI]?.type),
    );
  });
export const canvasPosition = createSelector(
  selectEditorFeature,
  ({ scenes, currentScene }) => {
    if (!currentScene || currentScene.length < 2) {
      return;
    }
    const mainScene = currentScene[0];
    const subScene = currentScene[1];
    const mainSceneIndex = scenes.findIndex(s => s.name == mainScene);
    const subSceneIndex = scenes[mainSceneIndex].subscenes.findIndex(
      s => s.name == subScene,
    );
    return scenes[mainSceneIndex].subscenes[subSceneIndex].position;
  },
);

export const selectTextDescriptorByIRI = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    ({ descriptors }): TextShapeDescriptor =>
      (descriptors[IRI] as TextShapeDescriptor)?.textConfig,
  );

export const textAttributeState = createSelector(
  selectEditorFeature,
  ({ selectedShapes, descriptors }) => {
    const textShapes = Object.keys(selectedShapes)
      .map(IRI => descriptors[IRI])
      .filter(descriptor => descriptor?.type == 'text-shape');

    if (!textShapes.length) {
      return {
        type: 'hide',
      };
    }

    return {
      type: 'show',
      text: (textShapes[0] as TextShapeDescriptor).text,
      fontSize: (textShapes[0] as TextShapeDescriptor).fontSize,
      letterSpacing: (textShapes[0] as TextShapeDescriptor).letterSpacing,
    };
  },
);

export const selectStateOptions = createSelector(selectEditorFeature, state => {
  const { descriptors, selectedShapes } = state;
  const IRI = Object.keys(selectedShapes)[0];
  if (!IRI) {
    return [];
  }

  // TODO - migrate
  return [];
  // return (
  //   (descriptors[IRI] as ImportedShapeDescriptor)?.baseShapeDescriptor
  //     ?.states || []
  // );
});

export const boundingRectangle = createSelector(
  selectEditorFeature,
  ({ bBoxes }) => {
    if (Object.keys(bBoxes).length == 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    let xMin = Infinity,
      yMin = Infinity,
      xMax = -Infinity,
      yMax = -Infinity;

    if (Object.keys(bBoxes).length) {
      Object.values(bBoxes).map(({ x, y, width, height }) => {
        xMin = x < xMin ? x : xMin;
        yMin = y < yMin ? y : yMin;
        xMax = x + width > xMax ? x + width : xMax;
        yMax = y + height > yMax ? y + height : yMax;
      });
    }

    return {
      x: xMin,
      y: yMin,
      width: xMax - xMin,
      height: yMax - yMin,
    };
  },
);

// export const selectCurrentFileIRI = createSelector(
//   selectEditorFeature,
//   state => state.currentFileIRI,
// );
export const selectNoAnimationMode = createSelector(
  selectEditorFeature,
  state => state.noAnimationMode,
);
export const selectIsFileLoading = createSelector(
  selectEditorFeature,
  state => state.isFileLoading,
);
export const selectFontLoaded = createSelector(
  selectEditorFeature,
  state => state.fontLoaded,
);

export const selectFiles = createSelector(
  selectEditorFeature,
  state => state.files,
);

export const selectProjects = createSelector(
  selectEditorFeature,
  state => state.projects,
);

// Current project from Editor state
export const selectCurrentProjectID = createSelector(
  selectEditorFeature,
  state => state.currentProjectID,
);

export const selectCurrentProjectIRI = createSelector(
  selectCurrentProjectID,
  id => (id ? IdToIRI(id) : undefined),
);

export const selectCurrentProjectFromEditor = createSelector(
  selectEditorFeature,
  selectCurrentProjectIRI,
  ({ projects }, iri) => (iri ? projects?.[iri] : undefined),
);

export const selectCurrentProjectLabelFromEditor = createSelector(
  selectCurrentProjectFromEditor,
  project => project?.literals?.label || 'Untitled Project',
);

export const selectPreviewShapeLoading = createSelector(
  selectEditorFeature,
  state => state.previewShapeLoading,
);

export const selectSlides = createSelector(selectEditorFeature, ({ files }) => {
  return Object.values(files || {}).sort(
    (file1, file2) =>
      file1.literals?.descriptor?.index - file2.literals?.descriptor?.index,
  );
});

export const selectNewProjectName = createSelector(
  selectEditorFeature,
  state => state.newProjectName,
);
export const loadedScenes = createSelector(
  selectEditorFeature,
  state => state.loadedScenes,
);

export const selectOpacityByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    const opacity = state.currentDescriptors[IRI]?.opacity;
    return isNaN(opacity) ? 1 : opacity;
  });

export const selectFloatEffectByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return state.currentDescriptors[IRI]?.floatEffect;
  });

export const selectRotateEffectByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return state.currentDescriptors[IRI]?.rotateEffect;
  });
export const selectPathSectionsByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return (state.currentDescriptors[IRI] as PathShapeDescriptor)?.sections;
  });

export const selectArmShapeConfig = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return (state.currentDescriptors[IRI] as ArmShapeDescriptor)?.config;
  });

export const selectHandShapeConfig = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return (state.currentDescriptors[IRI] as HandShapeNewDescriptor)?.config;
  });

export const selectConstraintByIRI = (IRI: string) =>
  createSelector(selectEditorFeature, state => {
    return state.currentDescriptors[IRI]?.constraints;
  });

export const selectDescriptorValueByIRI = (IRI: string, key: string) =>
  createSelector(
    selectEditorFeature,
    ({ descriptors }) => descriptors[IRI]?.[key],
  );
export const selectArcIntervalByIRI = (IRI: string) =>
  createSelector(
    selectEditorFeature,
    ({ descriptors: currentDescriptors }) =>
      (currentDescriptors[IRI] as CircleShapeDescriptor)?.arcInterval,
  );

// Helper function to calculate absolute cellAlignment
const calculateAbsoluteCellAlignment = (
  shapeIRI: string,
  descriptors: Record<string, GeneralShapeDescriptor>,
  shapes: Record<string, ResourceData>,
  visited = new Set<string>(),
): CellAlignment | null => {
  // Prevent infinite recursion
  if (visited.has(shapeIRI)) {
    return null;
  }
  visited.add(shapeIRI);

  const descriptor = descriptors[shapeIRI];
  const shape = shapes[shapeIRI];

  if (!descriptor || !shape) {
    return null;
  }

  const cellAlignment = descriptor.cellAlignment;
  if (!cellAlignment) {
    return null;
  }

  // console.log('cellAlignment --------------', cellAlignment);

  // Find parent shape
  const parentIRI =
    (shape.relationships?.parent as string) ||
    (shape.relationships?.file as string) ||
    (shape.relationships.childOf as string);
  if (!parentIRI) {
    // This is a root shape, return its cellAlignment as absolute
    return cellAlignment;
  }

  // Get parent's absolute cellAlignment
  const parentAbsoluteAlignment = calculateAbsoluteCellAlignment(
    parentIRI,
    descriptors,
    shapes,
    visited,
  );

  if (!parentAbsoluteAlignment) {
    // If parent has no alignment, use current alignment as absolute
    return cellAlignment;
  }

  // Calculate absolute position by adding parent's absolute position
  return {
    x: (parentAbsoluteAlignment.x || 0) + (cellAlignment.x || 0),
    y: (parentAbsoluteAlignment.y || 0) + (cellAlignment.y || 0),
    w: cellAlignment.w,
    h: cellAlignment.h,
    align: cellAlignment.align,
    margin: cellAlignment.margin,
  };
};

export const selectAbsoluteCellAlignments = createSelector(
  selectEditorFeature,
  ({ descriptors, shapes, files }) => {
    if (!descriptors || !shapes) {
      return {};
    }

    const result: Record<string, CellAlignment> = {};

    /* console.log('descriptors', descriptors);
    console.log(
      'shapes',
      Object.values(shapes).map(shape => shape.relationships),
    );
    console.log(
      'files',
      Object.values(files).map(file => {
        console.log('fileDescriptors', descriptors[file.IRI]?.cellAlignment);
        Object.keys(file.relationships.shapes).map(shapeIRI => {
          console.log('shapeDescriptors', descriptors[shapeIRI].cellAlignment);
        });
        // return shape.relationships;
      }),
    ); */
    // -- //
    Object.keys(descriptors).forEach(shapeIRI => {
      const absoluteAlignment = calculateAbsoluteCellAlignment(
        shapeIRI,
        descriptors,
        { ...shapes, ...files },
        new Set(),
      );

      if (absoluteAlignment) {
        result[shapeIRI] = absoluteAlignment;
      }
    });

    return result;
  },
);

export interface ContainerRectangle {
  IRI: string;
  type: 'container-shape' | 'text-shape' | 'image-shape';
  state: 'passive' | 'hover' | 'active';
  alignment: {
    x: number;
    y: number;
    w: number;
    h: number;
  };
}

export const selectContainerTree = createSelector(
  selectEditorFeature,
  ({ descriptors, files, shapes, currentFileIRI }) => {
    if (!descriptors || !shapes || !currentFileIRI) {
      return null;
    }

    if (!files[currentFileIRI]) {
      return null;
    }

    const buildTree = (resource: ResourceData): any => {
      return {
        IRI: resource.IRI,
        descriptor: descriptors[resource.IRI],
        children: Object.keys(resource.relationships?.children || {}).map(key =>
          buildTree(shapes[key]),
        ),
      };
    };

    return buildTree(files[currentFileIRI]);
  },
);

export const selectContainerState = createSelector(
  selectEditorFeature,
  ({
    descriptors,
    files,
    shapes,
    currentFileIRI,
    selectedShapes,
  }): Record<string, 'passive' | 'hover' | 'active'> => {
    // -- // -- //
    if (Object.keys(selectedShapes).length == 0) {
      return {
        [currentFileIRI]: 'hover',
      };
    }

    const selectedShape = Object.keys(selectedShapes)[0];

    // How do I get the ancestors of the selected shape?

    if (!selectedShape || !shapes[selectedShape]) {
      return {};
    }
    const shapesWithState = {};

    // -- // -- //
    // RootShape is Added to the parent -> let's make them resources

    let parent = shapes[selectedShape].relationships.parent as string;
    const siblings = Object.keys(
      shapes[parent]?.relationships.children || {},
    ) as string[];

    for (const sibling of siblings) {
      if (selectedShapes[sibling]) {
        shapesWithState[sibling] = 'active';
        continue;
      }
      shapesWithState[sibling] = 'hover';
    }

    while (parent) {
      shapesWithState[parent] = 'passive';
      parent = shapes[parent].relationships.parent as string;
    }

    shapesWithState[selectedShape] = 'active';
    // -- // -- //
    return shapesWithState;
  },
);

export const selectContainerRectangles = createSelector(
  selectEditorFeature,
  selectContainerState,
  selectAbsoluteCellAlignments,
  (
    { descriptors, files, shapes, currentFileIRI },
    shapeStates,
    cellAlignments,
  ) => {
    // console.log('selectContainerRectangles', { shapeStates, cellAlignments }); //
    // -- // -- // -- // -- //
    return (
      Object.entries(shapeStates)
        // .filter(([IRI]) => descriptors[IRI]?.type === 'container') //
        .map(
          ([IRI, state]) =>
            ({
              IRI,
              state,
              alignment: cellAlignments[IRI],
              type: descriptors[IRI]?.type || 'container-shape',
            }) as ContainerRectangle,
        )
    );
  },
);
